// ===============================================
// 5. CollapsibleFilters.jsx - NEW COMPONENT (Desktop)
// ===============================================

import { useState, useEffect } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  IconButton, 
  Collapse, 
  FormControl,
  Select,
  MenuItem,
  Rating,
  FormControlLabel,
  Checkbox,
  Tooltip,
  useTheme, 
  useMediaQuery 
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon 
} from '@mui/icons-material';

const CollapsibleFilters = ({ 
  category, 
  setCategory, 
  categoryOptions,
  maxDistance,
  setMaxDistance,
  ratingFilter,
  setRatingFilter,
  showInsidersOnly,
  setShowInsidersOnly,
  handleDiscoverFromNetwork,
  isAuthenticated,
  userCoordinates,
  DISTANCE_OPTIONS
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);

  return (
    <Paper 
      variant="outlined" 
      sx={{ p: 2, border: 'none' }}
      component="aside"
      aria-label="Search filters"
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: isExpanded ? 2 : 0
      }}>
        <Typography 
          variant="h6" 
          component="h3"
        >
          Filters
        </Typography>
        {isMobile && (
          <IconButton 
            onClick={() => setIsExpanded(!isExpanded)}
            size="small"
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>
      
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Category
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Select business category"
            >
              {categoryOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography>{option.label}</Typography>
                  <Typography color="text.secondary">({option.count})</Typography>
                </Box>
              </MenuItem>
            ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" gutterBottom>
            Distance
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Select
              value={maxDistance}
              onChange={(e) => setMaxDistance(e.target.value)}
              disabled={userCoordinates.loading}
              aria-label="Select maximum distance"
            >
              {DISTANCE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {userCoordinates.error && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {userCoordinates.error}
              </Typography>
            )}
          </FormControl>

          <Typography variant="subtitle2" gutterBottom>
            Minimum Rating
          </Typography>
          <Rating
            value={ratingFilter}
            onChange={(e, newValue) => setRatingFilter(newValue)}
            precision={0.5}
            sx={{ mb: 3 }}
            aria-label="Select minimum rating filter"
          />

          {/* PRESERVING EXISTING CACHE FUNCTIONALITY */}
          <Collapse in={isAuthenticated}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInsidersOnly}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setShowInsidersOnly(isChecked);
                    handleDiscoverFromNetwork(isChecked); // EXISTING CACHE LOGIC
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">
                      Show Insider Connections Only
                    </Typography>
                    <Tooltip title="Shows results from businesses connected through your network">
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
              }
              sx={{ mb: 2 }}
            />
          </Collapse>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsibleFilters;